import { FC } from "react";

import { FormType } from "@simplyk/common";
import { UseFormSetValue, UseFormTrigger } from "react-hook-form";

import { useFreePaymentSubmit } from "../../../blocks/payment/PaymentSection/FreePaymentProcessor/useFreePaymentSubmit";
import PaymentErrorAlert from "../../../blocks/payment/PaymentSection/PaymentElement/PaymentErrorAlert";
import { useFormV2Context } from "../../../contexts/FormV2Context";

import { useEnterKeyListener } from "@/canvas/features/FormV2/hooks/useEnterKeyListener";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { HeartsFormButton } from "@/forms-design-system/HeartsFormButton/HeartsFormButton";

interface SubmitFreeTicketingButtonProps {
  isEnterKeyListenerEnabled?: boolean;
}

export const SubmitFreeTicketingButton: FC<SubmitFreeTicketingButtonProps> = ({ isEnterKeyListenerEnabled }) => {
  const { t } = useTranslate();
  const { formObject: ticketing, form, currentStep } = useFormV2Context(FormType.Ticketing);
  const { handleSubmit, formState, setValue, trigger } = form;
  const { errors } = formState;
  const { validDiscount } = useFormV2Context(FormType.Ticketing);
  const { isPreview } = usePreviewContext();

  const { onSubmit, paymentError, paymentInProgress } = useFreePaymentSubmit({
    handleSubmit,
    formObject: ticketing,
    validDiscount,
    setValue: setValue as UseFormSetValue<TicketingPaymentInput | DonationFormPaymentInput>,
    trigger: trigger as UseFormTrigger<TicketingPaymentInput | DonationFormPaymentInput>,
    errors,
    formId: ticketing.id,
    label: "",
    stripeProductId: undefined,
    formType: FormType.Ticketing,
  });

  const isSubmitDisabled = isPreview || paymentInProgress;

  const submit = async () => {
    if (isSubmitDisabled) {
      return;
    }
    onSubmit();
  };

  useEnterKeyListener(submit, (isEnterKeyListenerEnabled ?? currentStep === "payment") && !isSubmitDisabled);

  return (
    <>
      <PaymentErrorAlert paymentError={paymentError} />
      <HeartsFormButton
        onClick={submit}
        disabled={isSubmitDisabled}
        data-test="submit-free-ticketing-button"
        animated={formState.isValid}
        loop={true}
      >
        {t("common", "confirm")}
      </HeartsFormButton>
    </>
  );
};
